import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {CreateUserPopupModel} from '../model/create-user-popup.model';
import {AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators} from '@angular/forms';
import {KeycloakApplicationRolesEnum} from '@core/modules/rest/user/enums/keycloak-application-roles.enum';
import {TranslateService} from '@ngx-translate/core';
import {SettingsEnum} from '@core/enums/settings.enum';
import {SliderToggleResponseModel} from '@shared/components/slide-toggle/model/slider-toggle-response.model';
import {NameOrEmailRequired} from '../../../../utils';

@Component({
    selector: 'app-user-info',
    templateUrl: './user-info.component.html',
    styleUrls: ['./user-info.component.scss']
})
export class UserInfoComponent implements OnInit, OnChanges {
    @Input() usernameExists: boolean;
    @Input() emailExists: boolean;
    @Input() selectedRoles: KeycloakApplicationRolesEnum[];
    @Output() closeDialogEvent: EventEmitter<void> = new EventEmitter<void>();
    @Output() validateFormEvent = new EventEmitter<{ name: string, email: string }>();
    @Output() createUserEvent: EventEmitter<CreateUserPopupModel> = new EventEmitter();
    @Output() resetEmailExistValidationEvent: EventEmitter<void> = new EventEmitter();
    @Output() roleChangeEvent: EventEmitter<KeycloakApplicationRolesEnum> = new EventEmitter();
    icon: string;

    disabled: boolean;
    patientLabel: string | undefined;
    doctorLabel: string | undefined;
    adminLabel: string | undefined;
    editorLabel: string | undefined;
    patientRole = KeycloakApplicationRolesEnum.PATIENT;
    doctorRole = KeycloakApplicationRolesEnum.DOCTOR;
    adminRole = KeycloakApplicationRolesEnum.ADMIN;
    editorRole = KeycloakApplicationRolesEnum.CONTENTEDITOR;
    form: FormGroup;
    nameControl: FormControl = new FormControl('', [Validators.required, this.nameValid()]);
    emailControl: FormControl = new FormControl('', [Validators.required, Validators.email, this.emailValid(), this.emailExist()]);

    rolesControl = new FormControl({value: ''});
    basicModeControl = new FormControl({value: ''});
    basicModeEnum = SettingsEnum.BASICMODE;
    patientRoleSelected = true;
    otherRoleSelected = false;
    isBasicMode = true;

    constructor(private translate: TranslateService) {
    }

    ngOnInit(): void {
        this.form = new FormGroup({
            name: this.nameControl,
            email: this.emailControl,
            roles: this.rolesControl,
            basicMode: this.basicModeControl
        }, {validators: [NameOrEmailRequired(this.nameControl, this.emailControl)]});

        this.patientLabel = this.translate.instant('ROLE.PATIENT');
        this.doctorLabel = this.translate.instant('ROLE.DOCTOR');
        this.editorLabel = this.translate.instant('ROLE.CONTENTEDITOR');
        this.adminLabel = this.translate.instant('ROLE.ADMIN');
        this.selectedRoles.push(this.patientRole);
    }

    saveUser(): void {
        const userObject: CreateUserPopupModel = {
            name: this.form.value.name,
            email: this.form.value.email,
            roles: this.selectedRoles,
            basicMode: this.isBasicMode
        };
        this.createUserEvent.emit(userObject);
    }

    nameChangeClick(): void {
        this.validateFormEvent.emit({name: this.nameControl.value, email: this.emailControl.value});
    }

    emailChangeClick(): void {
        this.validateFormEvent.emit({name: this.nameControl.value, email: this.emailControl.value});
    }

    roleChange(role: KeycloakApplicationRolesEnum): void {
        this.roleChangeEvent.emit(role);
        this.patientRoleSelected = role === this.patientRole && this.selectedRoles.includes(role);
        this.otherRoleSelected = role !== this.patientRole &&
            (this.selectedRoles.includes(this.doctorRole) || this.selectedRoles.includes(this.adminRole) || this.selectedRoles.includes(this.editorRole));
    }

    change(event: SliderToggleResponseModel): void {
        this.isBasicMode = event.selected;
    }

    nameValid(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const name = control.value;
            const isValid = name && name.length > 3;
            return isValid ? null : {nameValid: true};
        };
    }

    emailValid(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const email = control.value;
            const isValid = (email && !this.emailControl.invalid) || email.length === 0;
            return isValid ? null : {emailValid: true};
        };
    }

    emailExist(): ValidatorFn {
        return (_: AbstractControl): ValidationErrors | null => {
            return this.emailExists ? {emailExists: true} : null;
        };
    }

    ngOnChanges(changes: SimpleChanges): any {
        this.nameControl.updateValueAndValidity();
        this.emailControl.updateValueAndValidity();
    }
}
